import React, { useState } from "react"
import { Card, Button, Table, Tag, Flex, Typography } from "antd"
import { DeleteOutlined, CheckOutlined } from "@ant-design/icons"
import { spacings } from "themes/variables"
import PropTypes from "prop-types"
import styles from "./index.module.css"

const { Title, Text } = Typography

/**
 * ExperiencesCard component displays assigned experiences table and mobile cards
 * @param {Array} experiences - Array of assigned experiences
 * @param {number} totalCount - Total count of experiences
 * @param {Function} onAssign - Handler for assign button click
 * @param {Function} onRemove - Handler for remove experience
 * @param {boolean} checkingConflicts - Whether conflict check is in progress
 * @param {Function} t - Translation function
 */
const ExperiencesCard = ({ experiences = [], totalCount, onAssign, onRemove, checkingConflicts = false, t }) => {
  const [showAllExperiences, setShowAllExperiences] = useState(false)

  // Table columns for assigned experiences
  const experienceColumns = [
    {
      title: t("equipment.details.columns.experience_name"),
      dataIndex: "title",
      key: "title",
      render: (_, record) => record.title
    },
    {
      title: t("equipment.details.columns.guests"),
      dataIndex: "max_guests",
      key: "max_guests",
      render: (_, record) => record.max_guests
    },
    {
      title: t("equipment.details.columns.price"),
      key: "price",
      render: (_, record) => {
        const minPrice = Number.parseFloat(record.min_price || 0)
        const currency = record.currency || "$"
        return `${currency}${minPrice.toFixed(2)}`
      }
    },
    {
      title: t("equipment.details.columns.status"),
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const isPublished = status.toLowerCase() === "published"
        const displayStatus = status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
        return (
          <Tag color={isPublished ? "green" : "default"} icon={isPublished ? <CheckOutlined /> : null} className={styles.statusTag}>
            {displayStatus}
          </Tag>
        )
      }
    },
    {
      title: "",
      key: "action",
      align: "right",
      width: 50,
      render: (_, record) => {
        const isOnlyExperience = experiences.length === 1
        return (
          <Button
            type="text"
            icon={<DeleteOutlined />}
            className={styles.deleteButton}
            onClick={() => onRemove(record.id)}
            disabled={isOnlyExperience || checkingConflicts}
            loading={checkingConflicts}
            title={
              isOnlyExperience
                ? t("equipment.details.cannot_remove_last_experience", { defaultValue: "Cannot remove the last experience" })
                : ""
            }
          />
        )
      }
    }
  ]

  return (
    <Card className={styles.experiencesCard}>
      <Flex justify="space-between" align="center" className={styles.experienceHeader}>
        <Title level={4}>
          {t("equipment.details.assigned_experiences")} <span className={styles.totalCount}> ({totalCount})</span>
        </Title>
        <Button type="link" className={styles.assignLink} onClick={onAssign}>
          {t("equipment.details.assign")}
        </Button>
      </Flex>

      {experiences.length > 0 ? (
        <>
          {/* Desktop Table View */}
          <div className={styles.scrollContainer}>
            <Table
              dataSource={experiences}
              columns={experienceColumns}
              pagination={false}
              rowKey="id"
              className={styles.experiencesTable}
              scroll={{ x: 600 }}
            />
          </div>

          {/* Mobile Card View */}
          <div className={styles.mobileExperiencesContainer}>
            <div className={styles.mobileExperiencesHeader}>
              <Text className={styles.mobileExperiencesHeaderText}>{t("equipment.details.assigned_experiences")}</Text>
            </div>
            {(showAllExperiences ? experiences : experiences.slice(0, 3)).map((experience) => {
              const isPublished = experience.status?.toLowerCase() === "published"
              const displayStatus = experience.status?.charAt(0).toUpperCase() + experience.status?.slice(1).toLowerCase()
              const minPrice = Number.parseFloat(experience.min_price || 0)
              const maxPrice = Number.parseFloat(experience.max_price || 0)
              const currency = experience.currency || "$"
              const priceDisplay =
                minPrice === maxPrice
                  ? `${currency}${minPrice.toFixed(2)}`
                  : `${currency}${minPrice.toFixed(2)} - ${currency}${maxPrice.toFixed(2)}`
              const isOnlyExperience = experiences.length === 1

              return (
                <div key={experience.id} className={styles.mobileExperienceCard}>
                  <Flex justify="space-between" align="flex-start" className={styles.experienceCardRow}>
                    <Flex align="center" gap={spacings["XS"]} className={styles.experienceCardLeft}>
                      <Tag
                        color={isPublished ? "green" : "default"}
                        icon={isPublished ? <CheckOutlined /> : null}
                        className={styles.experienceStatusTag}
                      >
                        {displayStatus}
                      </Tag>
                      <Text className={styles.experienceName}>{experience.title}</Text>
                    </Flex>
                    <Button
                      type="text"
                      icon={<DeleteOutlined />}
                      className={styles.deleteButton}
                      onClick={() => onRemove(experience.id)}
                      disabled={isOnlyExperience || checkingConflicts}
                      loading={checkingConflicts}
                    />
                  </Flex>
                  <Text type="secondary" className={styles.experienceCardDetails}>
                    {experience.max_guests} {t("equipment.details.columns.guests")} • {priceDisplay}
                  </Text>
                </div>
              )
            })}

            {experiences.length > 3 && (
              <div className={styles.mobileExperienceSeeMore}>
                <Button type="link" onClick={() => setShowAllExperiences(!showAllExperiences)}>
                  {showAllExperiences ? t("equipment.details.see_less") : t("equipment.details.see_more")}
                </Button>
              </div>
            )}
          </div>
        </>
      ) : (
        <Flex vertical align="center" justify="center" className={styles.emptyState}>
          <div className={styles.emptyStateImage}>
            <Text type="secondary">{t("equipment.details.no_illustration")}</Text>
          </div>
          <Text type="secondary">{t("equipment.details.not_adding_to_listings")}</Text>
          <Button type="primary" className={styles.assignButton} onClick={onAssign}>
            {t("equipment.details.assign")}
          </Button>
        </Flex>
      )}
    </Card>
  )
}

ExperiencesCard.propTypes = {
  experiences: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      title: PropTypes.string,
      max_guests: PropTypes.number,
      min_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      max_price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      currency: PropTypes.string,
      status: PropTypes.string
    })
  ),
  totalCount: PropTypes.number,
  onAssign: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  checkingConflicts: PropTypes.bool,
  t: PropTypes.func.isRequired
}

ExperiencesCard.defaultProps = {
  experiences: [],
  totalCount: 0
}

export default ExperiencesCard
